/**
 * 颜色工具函数
 * 用于项目卡片差异化着色
 */

// ========== 基于项目ID的随机着色（保留兼容） ==========

// 预定义的淡色背景色板（12种）
export const PROJECT_CARD_COLORS = [
  '#f0f5ff',  // 淡蓝
  '#f6ffed',  // 淡绿
  '#fff7e6',  // 淡橙
  '#fff1f0',  // 淡红
  '#f9f0ff',  // 淡紫
  '#e6fffb',  // 淡青
  '#fcffe6',  // 淡黄绿
  '#fff0f6',  // 淡粉
  '#e6f4ff',  // 天蓝
  '#f4ffb8',  // 柠檬
  '#d6e4ff',  // 薰衣草
  '#ffd6e7',  // 樱花粉
];

// 对应的边框色（稍深一点）
export const PROJECT_CARD_BORDER_COLORS = [
  '#adc6ff',  // 淡蓝边框
  '#b7eb8f',  // 淡绿边框
  '#ffd591',  // 淡橙边框
  '#ffa39e',  // 淡红边框
  '#d3adf7',  // 淡紫边框
  '#87e8de',  // 淡青边框
  '#eaff8f',  // 淡黄绿边框
  '#ffadd2',  // 淡粉边框
  '#91caff',  // 天蓝边框
  '#d3f261',  // 柠檬边框
  '#adc6ff',  // 薰衣草边框
  '#ffadd2',  // 樱花粉边框
];

/**
 * 根据项目ID生成颜色索引
 * 使用哈希算法确保同一项目每次显示颜色一致
 */
export const getProjectColorIndex = (projectId: string): number => {
  let hash = 0;
  for (let i = 0; i < projectId.length; i++) {
    hash = projectId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return Math.abs(hash) % PROJECT_CARD_COLORS.length;
};

/**
 * 根据项目ID获取背景色
 */
export const getProjectBackgroundColor = (projectId: string): string => {
  const index = getProjectColorIndex(projectId);
  return PROJECT_CARD_COLORS[index];
};

/**
 * 根据项目ID获取边框色
 */
export const getProjectBorderColor = (projectId: string): string => {
  const index = getProjectColorIndex(projectId);
  return PROJECT_CARD_BORDER_COLORS[index];
};

/**
 * 根据项目ID获取完整的颜色配置
 */
export const getProjectColors = (projectId: string): {
  backgroundColor: string;
  borderColor: string;
  index: number;
} => {
  const index = getProjectColorIndex(projectId);
  return {
    backgroundColor: PROJECT_CARD_COLORS[index],
    borderColor: PROJECT_CARD_BORDER_COLORS[index],
    index
  };
};


// ========== 基于预算使用率的渐变着色（新增） ==========

/**
 * 预算状态类型
 */
export type BudgetColorStatus = 'healthy' | 'good' | 'attention' | 'warning' | 'danger' | 'over' | 'unset';

/**
 * 基于预算的颜色配置
 */
export interface BudgetBasedColors {
  backgroundColor: string;
  borderColor: string;
  status: BudgetColorStatus;
  statusText: string;
  percentage: number;
}

/**
 * 预算颜色配置表
 * 从健康（绿）到超支（红）的渐变色系
 */
const BUDGET_COLOR_CONFIG: Record<BudgetColorStatus, { bg: string; border: string; text: string }> = {
  healthy: { bg: '#f6ffed', border: '#b7eb8f', text: '健康' },      // 极浅绿 (0-30%)
  good: { bg: '#d9f7be', border: '#95de64', text: '良好' },         // 浅绿 (30-50%)
  attention: { bg: '#fffbe6', border: '#ffe58f', text: '注意' },    // 浅黄 (50-70%)
  warning: { bg: '#fff1b8', border: '#ffc53d', text: '警告' },      // 黄 (70-85%)
  danger: { bg: '#fff2e8', border: '#ffbb96', text: '紧张' },       // 浅橙 (85-100%)
  over: { bg: '#fff1f0', border: '#ffa39e', text: '超支' },         // 浅红 (>100%)
  unset: { bg: '#fafafa', border: '#d9d9d9', text: '未设置' }       // 灰色 (无预算)
};

/**
 * 根据预算使用率获取状态
 * @param percentage 预算使用百分比
 * @returns 预算状态
 */
export const getBudgetStatus = (percentage: number): BudgetColorStatus => {
  if (percentage <= 30) return 'healthy';
  if (percentage <= 50) return 'good';
  if (percentage <= 70) return 'attention';
  if (percentage <= 85) return 'warning';
  if (percentage <= 100) return 'danger';
  return 'over';
};

/**
 * 根据预算和实际成本获取颜色配置
 * @param budget 预算金额
 * @param actualCost 实际成本
 * @returns 颜色配置对象
 */
export const getBudgetBasedColors = (budget: number, actualCost: number): BudgetBasedColors => {
  // 未设置预算
  if (!budget || budget === 0) {
    const config = BUDGET_COLOR_CONFIG.unset;
    return {
      backgroundColor: config.bg,
      borderColor: config.border,
      status: 'unset',
      statusText: config.text,
      percentage: 0
    };
  }
  
  const percentage = (actualCost / budget) * 100;
  const status = getBudgetStatus(percentage);
  const config = BUDGET_COLOR_CONFIG[status];
  
  return {
    backgroundColor: config.bg,
    borderColor: config.border,
    status,
    statusText: config.text,
    percentage
  };
};

/**
 * 获取预算状态对应的图标
 * @param status 预算状态
 * @returns 图标字符
 */
export const getBudgetStatusIcon = (status: BudgetColorStatus): string => {
  const icons: Record<BudgetColorStatus, string> = {
    healthy: '✅',
    good: '🔵',
    attention: '⚠️',
    warning: '🟡',
    danger: '🔴',
    over: '❌',
    unset: '⚪'
  };
  return icons[status];
};

/**
 * 获取预算状态对应的 Ant Design Tag 颜色
 * @param status 预算状态
 * @returns Tag 颜色
 */
export const getBudgetTagColor = (status: BudgetColorStatus): string => {
  const colors: Record<BudgetColorStatus, string> = {
    healthy: 'success',
    good: 'processing',
    attention: 'warning',
    warning: 'warning',
    danger: 'error',
    over: 'error',
    unset: 'default'
  };
  return colors[status];
};
